/**
 * JOY OURS Quote System
 * Stores and manages quote-list products in the customer's browser.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'joyours_quote_list_v1';
  const CHANGE_EVENT = 'joyours:quote-change';

  function normaliseQuantity(quantity) {
    const parsedQuantity = Number.parseInt(quantity, 10);

    if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) {
      return 1;
    }

    return Math.min(parsedQuantity, 999);
  }

  function normaliseText(value) {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  function normaliseItem(item) {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const variantId = normaliseText(item.variantId);

    if (!variantId) {
      return null;
    }

    return {
      productId: normaliseText(item.productId),
      variantId: variantId,
      productTitle: normaliseText(item.productTitle),
      variantTitle: normaliseText(item.variantTitle),
      sku: normaliseText(item.sku),
      url: normaliseText(item.url),
      image: normaliseText(item.image),
      price: normaliseText(item.price),
      quantity: normaliseQuantity(item.quantity)
    };
  }

  function readItems() {
    try {
      const storedValue = window.localStorage.getItem(STORAGE_KEY);

      if (!storedValue) {
        return [];
      }

      const parsedValue = JSON.parse(storedValue);

      if (!Array.isArray(parsedValue)) {
        return [];
      }

      return parsedValue
        .map(normaliseItem)
        .filter(Boolean);
    } catch (error) {
      console.warn('JOY OURS Quote System could not read the quote list.', error);
      return [];
    }
  }

  function saveItems(items) {
    const safeItems = Array.isArray(items)
      ? items.map(normaliseItem).filter(Boolean)
      : [];

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safeItems));
    } catch (error) {
      console.warn('JOY OURS Quote System could not save the quote list.', error);
    }

    announceChange(safeItems);

    return safeItems;
  }

  function getTotalQuantity(items) {
    return items.reduce(function (total, item) {
      return total + normaliseQuantity(item.quantity);
    }, 0);
  }

  function announceChange(items) {
    const detail = {
      items: items,
      itemCount: items.length,
      totalQuantity: getTotalQuantity(items)
    };

    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: detail
      })
    );

    updateBadges(detail.totalQuantity);
  }

  function updateBadges(totalQuantity) {
    const badges = document.querySelectorAll('[data-quote-count]');

    badges.forEach(function (badge) {
      badge.textContent = String(totalQuantity);
      badge.hidden = totalQuantity === 0;
      badge.setAttribute(
        'aria-label',
        totalQuantity + (totalQuantity === 1 ? ' product in quote list' : ' products in quote list')
      );
    });
  }

  function addItem(item) {
    const safeItem = normaliseItem(item);

    if (!safeItem) {
      return {
        success: false,
        reason: 'missing-variant-id',
        items: readItems()
      };
    }

    const items = readItems();
    const existingItem = items.find(function (savedItem) {
      return savedItem.variantId === safeItem.variantId;
    });

    if (existingItem) {
      existingItem.quantity = normaliseQuantity(
        existingItem.quantity + safeItem.quantity
      );

      Object.keys(safeItem).forEach(function (key) {
        if (key !== 'quantity' && safeItem[key]) {
          existingItem[key] = safeItem[key];
        }
      });
    } else {
      items.push(safeItem);
    }

    const savedItems = saveItems(items);

    return {
      success: true,
      items: savedItems,
      addedItem: safeItem
    };
  }

  function removeItem(variantId) {
    const safeVariantId = normaliseText(variantId);
    const items = readItems().filter(function (item) {
      return item.variantId !== safeVariantId;
    });

    return saveItems(items);
  }

  function updateQuantity(variantId, quantity) {
    const safeVariantId = normaliseText(variantId);
    const parsedQuantity = Number.parseInt(quantity, 10);

    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      return removeItem(safeVariantId);
    }

    const items = readItems();

    items.forEach(function (item) {
      if (item.variantId === safeVariantId) {
        item.quantity = normaliseQuantity(parsedQuantity);
      }
    });

    return saveItems(items);
  }

  function clearItems() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('JOY OURS Quote System could not clear the quote list.', error);
    }

    announceChange([]);

    return [];
  }

  function refreshBadges() {
    const items = readItems();
    updateBadges(getTotalQuantity(items));
  }

  window.JoyOursQuote = {
    storageKey: STORAGE_KEY,
    changeEvent: CHANGE_EVENT,
    getItems: readItems,
    getTotalQuantity: function () {
      return getTotalQuantity(readItems());
    },
    addItem: addItem,
    removeItem: removeItem,
    updateQuantity: updateQuantity,
    clearItems: clearItems,
    refreshBadges: refreshBadges
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshBadges);
  } else {
    refreshBadges();
  }
})();
